import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';

import { connect } from 'react-redux';

import { Button, Header, Title } from '../../../components';
import LogItemsCard from './logitems.card';
import DowntimeHistory from './downtimeHistory';

import { config } from '../../../api/config';
import { actions } from '../../../state';

import { formatLocaleDate, mapDevicesStatus } from '../../../utils';

import styles from './logDetails.module.scss';

const LogDetailsPage = ({ dispatch, match, history, deviceAnalytics }) => {
  const id = match.params.id;

  const clearDeviceAnalytics = useCallback(() => {
    dispatch(actions.monitoring.clearDeviceAnalyticsAction());
  }, [dispatch]);

  const fetchDeviceAnalyticsData = useCallback(id => {
    dispatch(actions.monitoring.getDeviceAnalyticsAction(id));
  }, []);

  useEffect(() => () => clearDeviceAnalytics(), [clearDeviceAnalytics]);

  useEffect(() => {
    fetchDeviceAnalyticsData(id);
  }, [fetchDeviceAnalyticsData, id]);

  const logDetailsData = useMemo(() => {
    if (deviceAnalytics) {
      const items = {
        'Įrenginio pavadinimas': deviceAnalytics.title || '-',
        ...(deviceAnalytics.current
          ? {
              'Data ir laikas':
                formatLocaleDate(deviceAnalytics.current.status_changed_at) ||
                '-',
            }
          : {}),
        Lokacija: deviceAnalytics.location_address || '-',
        ...(deviceAnalytics.current
          ? {
              Statusas:
                deviceAnalytics.current.status !== undefined &&
                deviceAnalytics.current.status !== null
                  ? mapDevicesStatus(deviceAnalytics.current.status)
                  : deviceAnalytics.current.status,
            }
          : {}),
        'Pranešimo tipas': deviceAnalytics.last_error || '-',
        'Klaidos pranešimas': deviceAnalytics.last_error || '-',
        ...(deviceAnalytics.downtime
          ? {
              Downtime: `${formatLocaleDate(
                deviceAnalytics.downtime.from || Date.now(),
                'yyyy-MM-dd HH:mm:ss'
              )} to ${formatLocaleDate(
                deviceAnalytics.downtime.to || Date.now(),
                'yyyy-MM-dd HH:mm:ss'
              )}`,
            }
          : {}),
      };
      return Object.entries(items).map(([name, value]) => {
        if (name === 'Statusas') {
          return { name, value, status: deviceAnalytics.current.status };
        }
        return { name, value };
      });
    }

    return [];
  }, [deviceAnalytics]);

  return (
    <section>
      <Header>
        <Title>{config.PAGES.logDetails}</Title>
      </Header>
      <div className={styles.logContainer}>
        <div className={styles.detailsWrapper}>
          {logDetailsData.map((item, index) => (
            <LogItemsCard
              key={index}
              name={item.name}
              value={item.value}
              status={item.status}
            />
          ))}
        </div>
        <DowntimeHistory downtimeHistory={deviceAnalytics?.downtime_history} />
        <Button
          color="outline"
          type="button"
          classes={{ button: styles.goBackButton }}
          onClick={() => history.goBack()}
        >
          Atgal
        </Button>
      </div>
    </section>
  );
};

const mapStateToProps = state => ({
  deviceAnalytics: state.monitoring.deviceAnalytics,
});

LogDetailsPage.propTypes = {
  dispatch: PropTypes.func.isRequired,
  match: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  deviceAnalytics: PropTypes.oneOfType([
    PropTypes.oneOf([null]),
    PropTypes.shape({
      device_id: PropTypes.number,
      title: PropTypes.string,
      location_address: PropTypes.string,
      last_error: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.oneOf([null]),
      ]),
      current: PropTypes.shape({
        status: PropTypes.number,
        duration_seconds: PropTypes.number,
        status_changed_at: PropTypes.string,
      }),
      downtime: PropTypes.oneOfType([
        PropTypes.oneOf([null]),
        PropTypes.shape({
          from: PropTypes.string,
          to: PropTypes.string,
        }),
      ]),
      downtime_history: PropTypes.arrayOf(
        PropTypes.shape({
          from: PropTypes.string,
          to: PropTypes.string,
        })
      ),
    }),
  ]),
};

export default connect(mapStateToProps)(LogDetailsPage);
